import axios from "axios";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from "./tokenStore";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const AI_INTERVIEW_API_BASE_URL =
  process.env.REACT_APP_AI_INTERVIEW_API_BASE_URL;

/**
 * Single axios instance for the Freela API.
 *
 * withCredentials is always on so the httpOnly refresh cookie travels with
 * every request; the access token is attached from memory by the request
 * interceptor rather than being re-read from localStorage at each call site.
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Refresh-on-401
//
// Previously nothing reacted to a 401: the token was refreshed on a blind
// 14-minute interval, so any request that expired mid-cycle simply failed.
// A 401 now triggers one refresh, and the original request is replayed. All
// requests that 401 while a refresh is in flight wait for that same refresh
// rather than each starting their own.
// ---------------------------------------------------------------------------

let refreshInFlight = null;
let onSessionExpired = () => {};

/** AuthContext registers what should happen when the session cannot be renewed. */
export const setSessionExpiredHandler = (fn) => {
  onSessionExpired = typeof fn === "function" ? fn : () => {};
};

export const refreshAccessToken = async () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE_URL}user/refreshToken`, {}, { withCredentials: true })
      .then((res) => {
        const token = res?.data?.data?.accessToken || res?.data?.accessToken;
        if (!token) throw new Error("No access token in refresh response");
        setAccessToken(token);
        return token;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isRefreshCall = String(original.url || "").includes(
      "user/refreshToken"
    );

    if (status !== 401 || original._retried || isRefreshCall) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      await refreshAccessToken();
      return api(original);
    } catch (refreshError) {
      clearAccessToken();
      onSessionExpired();
      return Promise.reject(error);
    }
  }
);

/**
 * Call the Freela API.
 *
 * Signature is unchanged from the original so existing call sites keep
 * working; `role` is still forwarded as a header.
 */
const ApiCall = async (
  method,
  endpoint,
  data = {},
  content,
  role,
  params = {},
  onUploadProgress = null
) => {
  try {
    const response = await api({
      method,
      url: endpoint,
      data,
      params,
      headers: {
        ...(content ? { "Content-Type": content } : {}),
        ...(role ? { role } : {}),
      },
      ...(onUploadProgress && { onUploadProgress }),
    });

    return response.data;
  } catch (error) {
    console.error(
      `API call error: ${error.response?.data?.message || error.message}`
    );
    throw error;
  }
};

export const AiInterviewApiCall = async ({
  method,
  endpoint,
  data = {},
  content,
  params = {},
  headers = {},
}) => {
  try {
    const response = await axios({
      method: method,
      url: `${AI_INTERVIEW_API_BASE_URL}${endpoint}`,
      data: data,
      params: params,
      headers: {
        "Content-Type": content,
        ...headers,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`AI Interview API call error: ${error.message}`);
    throw error;
  }
};

/**
 * Tenant-scoped interview calls.
 *
 * These used to send x-app-key / x-app-id / x-tenant-id read from
 * REACT_APP_* variables. Create React App inlines those into the built
 * bundle, so the credentials were readable by any visitor. The call is now
 * proxied through the Freela backend, which holds the key server-side.
 */
export const TenantInterviewApiCall = async ({
  method,
  endpoint,
  data = {},
  content = "application/json",
  params = {},
  headers = {},
}) => {
  try {
    const response = await api({
      method: "POST",
      url: "interview/tenant-proxy",
      data: {
        method,
        endpoint,
        body: data,
        query: params,
      },
      headers: {
        "Content-Type": content,
        ...headers,
      },
    });
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || "API call failed";
    console.error(`Tenant Interview API call error: ${errorMessage}`);

    const enhancedError = new Error(errorMessage);
    enhancedError.response = error.response;
    enhancedError.status = error.response?.status;
    throw enhancedError;
  }
};

export default ApiCall;
